import React, { useEffect } from "react";
import toast from "react-hot-toast";
import { Card, Title, Text, BarChart, LineChart } from "@tremor/react";
import * as Common from "../../../Components/Common";
import {
  getDailyTaskSummaryData,
  getAttendanceSummaryData,
} from "../../../Services/DashboardService";
import { TOAST_MESSAGES } from "../../../Constants/Strings";
import { useDashboard } from "../../../Context";
import { extractErrorMessage } from "../../../Utils/extractErrorMessage";

const AttendanceSummary = () => {
  const [taskData, setTaskData] = React.useState([]);
  const [dailyTaskData, setTaskDailyTaskData] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const { filters, getFormattedData } = useDashboard();

  const formatKeys = (data) => {
    const keyMap = {
      in_time: "In Time",
      late: "Late",
      backdated: "Backdated",
    };

    return Object.fromEntries(
      Object.entries(data).map(([key, value]) => [
        keyMap[key] || key, // convert if in map, otherwise keep original
        value,
      ])
    );
  };

  const fetchTaskData = () => {
    getAttendanceSummaryData(getFormattedData()).then((Response) => {
      if (Response.status === 200) {
        setTaskData(Response.data);
      } else {
        const msg = extractErrorMessage(Response) || TOAST_MESSAGES.FAILED_lOAD;
        toast.error(msg);
      }
    });
    setLoading(false);
  };

  const fetchDailyTaskData = () => {
    getDailyTaskSummaryData(getFormattedData()).then((Response) => {
      if (Response.status === 200) {
        setTaskDailyTaskData(Response.data);
      } else {
        const msg = extractErrorMessage(Response) || TOAST_MESSAGES.FAILED_lOAD;
        toast.error(msg);
      }
    });
    setLoading(false);
  };

  const toDecimalHours = (timeStr) => {
    if (!timeStr) return 0.00;
    const [h, m, s] = timeStr.split(":").map(Number);
    return +(h + m / 60 + s / 3600).toFixed(2);
  };

  const filteredData = dailyTaskData?.map((item) => ({
    date: item.date,
    CheckIn: toDecimalHours(item.CheckIn),
    CheckOut: toDecimalHours(item.CheckOut),
  }));

  const formattedData = taskData.user ? [formatKeys(taskData)] : [];
  const dateCount = filteredData.length;
  const widthPerDay = 60;
  const chartWidth = dateCount * widthPerDay;

  useEffect(() => {
    if (filters.user_id){
      fetchDailyTaskData();
      fetchTaskData();
    }
  }, [filters]);

  return (
    <>
      <div>
        <Card className="bg-white shadow-xl rounded-xl p-4 overflow-x-auto">
          <Title className="text-lg">Check-In / Check-Out Time</Title>
          <Text className="text-sm text-gray-500">Daily average in hours</Text>

          <div style={chartWidth ? { max_width: `${chartWidth}px` } : { width: "auto" }}>
            <LineChart
              className="mt-6 max-h-72"
              data={filteredData}
              index="date"
              categories={["CheckIn", "CheckOut"]}
              colors={["green", "red"]}
              yAxisWidth={80}
              intervalType={"all"}
              valueFormatter={(value) => {
                if (value === null || value === undefined) return "N/A";

                const hours = Math.floor(value);
                const minutes = Math.round((value - hours) * 60);

                return `${String(hours).padStart(2, "0")}:${String(
                  minutes,
                ).padStart(2, "0")}`;
              }}
            />
          </div>
        </Card>
      </div>
      <div>
        <Card className="bg-white shadow-xl rounded-xl p-4 overflow-x-auto min-w-full">
          <Title className="text-lg">Approved Attendance Summary</Title>
          <Text className="text-sm text-gray-500">
            User wise Attendance Summary
          </Text>

          {loading ? (
            <div className="h-72 flex items-center justify-center">
              <Common.Spinner />
            </div>
          ) : (
            <BarChart
              className="mt-6 h-72"
              data={formattedData || []}
              index="user"
              categories={["In Time", "Late", "Backdated"]}
              colors={["green-300", "blue-400", "red-400"]}
              showXAxis={true}
              yAxisWidth={80}
              showAnimation={true}
              animationDuration={1000}
              rotateLabelX={{ angle: 0, verticalShift: 10, xAxisHeight: 40 }}
              enableLegendSlider={true}
              valueFormatter={(v) => `${v} days`}
            />
          )}
        </Card>
      </div>
    </>
  );
};
export default AttendanceSummary;
